import React, { memo, useCallback, useContext, useMemo } from 'react';
import isNumber from 'lodash/isNumber';
import map from 'lodash/map';
import { BottomSheet, Header } from '@yme/atoms/bottom-sheet';
import { Button, CONTAINED_VARIANT, NO_ICON, SIZE_44 } from '@yme/atoms/button';
import { Checkbox } from '@yme/atoms/checkbox';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { FilterClearAll, FilterDropdownContext } from '@yme/molecules/filter-dropdown';
import { colorCoreBlack300, colorCoreBlack900Base, colorCoreGray1000Base, } from '@yme/tokens/colors';
import { RegularTextLevel2, RegularTextLevel3, RegularTextLevel4 } from '@yme/tokens/typography';
import { setCheckedStateForAll } from './helpers';
import { FilterItemDesktopStyled, FilterListStyled, FilterListWrapperStyled, ScrollContainerStyled, SelectAllStyled, } from './styled';
export const CheckboxesDropdown = memo((props) => {
    const { isRightToLeft, labels, id, hasAdditionalControl = true, isCounterAlwaysVisible = false, isCheckboxesViewSimple = false, isCloseOnSelect = false, onSelect, } = props;
    const { options, isExpanded, titleLabel, setOptions, setIsExpanded } = useContext(FilterDropdownContext);
    const { isInfiniteDesktopView: isDesktop } = useWindowSize();
    const isAllSelected = useMemo(() => options.every((item) => item.checked), [options]);
    const isSomeSelected = useMemo(() => options.some((item) => item.checked), [options]);
    const handleCloseOnSelect = useCallback(() => {
        if (isCloseOnSelect) {
            setIsExpanded(false);
        }
    }, [isCloseOnSelect, setIsExpanded]);
    const onOptionChange = useCallback((e) => {
        const data = options.map((opt) => {
            const { value, label, checked } = opt;
            if (value === e.target.id) {
                return {
                    value,
                    label,
                    checked: !checked,
                };
            }
            return {
                value,
                label,
                checked,
            };
        });
        setOptions(data);
        onSelect?.(data);
        handleCloseOnSelect();
    }, [handleCloseOnSelect, onSelect, options, setOptions]);
    const onSelectAllClicked = useCallback(() => {
        let data;
        if (isAllSelected) {
            data = setCheckedStateForAll(options, false);
        }
        else {
            data = setCheckedStateForAll(options, true);
        }
        setOptions(data);
        onSelect?.(data);
        handleCloseOnSelect();
    }, [handleCloseOnSelect, isAllSelected, onSelect, options, setOptions]);
    const clearSelected = useCallback(() => {
        const data = setCheckedStateForAll(options, false);
        setOptions(data);
        onSelect?.(data);
        handleCloseOnSelect();
    }, [handleCloseOnSelect, onSelect, options, setOptions]);
    const SelectAll = useMemo(() => (React.createElement(SelectAllStyled, null,
        React.createElement(Checkbox, { id: id, value: labels.filters_selectAll_label, checked: isSomeSelected || isAllSelected, onChange: onSelectAllClicked, hasMultipleChecked: !isAllSelected, isRightToLeft: isRightToLeft }))), [
        id,
        isAllSelected,
        isRightToLeft,
        isSomeSelected,
        labels.filters_selectAll_label,
        onSelectAllClicked,
    ]);
    const Items = useMemo(() => map(options, (opt, index) => {
        const { value, label, checked, count } = opt;
        const isCountValid = isNumber(count);
        const counterView = isCounterAlwaysVisible
            ? isNumber(count) && (React.createElement(RegularTextLevel4, { color: colorCoreBlack300, "aria-disabled": !isCountValid || (isCountValid && count === 0) }, count))
            : isCountValid &&
                !isDesktop && (React.createElement(RegularTextLevel4, { color: colorCoreBlack300, "aria-disabled": !isCountValid || (isCountValid && count === 0) }, count));
        return (React.createElement(FilterItemDesktopStyled, { key: index, isCheckboxesViewSimple: isCheckboxesViewSimple },
            React.createElement(Checkbox, { id: value, onChange: onOptionChange, value: label, checked: checked, isRightToLeft: isRightToLeft, disabled: isCountValid && count === 0 }),
            counterView));
    }), [
        options,
        isCounterAlwaysVisible,
        isDesktop,
        isCheckboxesViewSimple,
        onOptionChange,
        isRightToLeft,
    ]);
    const ListDesktop = useMemo(() => {
        // view not displayed
        if (!isExpanded) {
            return null;
        }
        // view without any control buttons (only items)
        if (isCheckboxesViewSimple) {
            return React.createElement(React.Fragment, null, Items);
        }
        // view with some extra functionality (select all and clear options tick)
        return (React.createElement(FilterListWrapperStyled, null,
            React.createElement(ScrollContainerStyled, { isRightToLeft: isRightToLeft },
                SelectAll,
                React.createElement(FilterListStyled, { isRightToLeft: isRightToLeft }, Items)),
            (isSomeSelected || isAllSelected) && (React.createElement(FilterClearAll, { isRightToLeft: isRightToLeft, onClickHandler: clearSelected, clearLabel: labels.filters_clearAll_label }))));
    }, [
        Items,
        SelectAll,
        clearSelected,
        isAllSelected,
        isCheckboxesViewSimple,
        isExpanded,
        isRightToLeft,
        isSomeSelected,
        labels.filters_clearAll_label,
    ]);
    const setIsExpandedHandler = useCallback(() => {
        setIsExpanded(false);
    }, [setIsExpanded]);
    const ListMobile = useMemo(() => (React.createElement(BottomSheet, { open: isExpanded, onDismiss: setIsExpandedHandler, header: React.createElement(Header, { headerLeft: React.createElement(RegularTextLevel2, { weight: 500 }, titleLabel), headerRight: hasAdditionalControl ? (React.createElement(RegularTextLevel3, { color: isSomeSelected ? colorCoreBlack900Base : colorCoreGray1000Base, as: "span", onClick: clearSelected }, labels.filters_clearAll_label)) : undefined }), footer: React.createElement(Button, { size: SIZE_44, hasIconButton: NO_ICON, buttonVariant: CONTAINED_VARIANT, 
            // todo: md: add labels once UI will be ready
            label: labels.common_confirm_button ?? 'show results', onClick: setIsExpandedHandler }) },
        React.createElement(FilterListWrapperStyled, null,
            hasAdditionalControl ? SelectAll : null,
            React.createElement(FilterListStyled, { isRightToLeft: isRightToLeft }, Items)))), [
        isExpanded,
        setIsExpandedHandler,
        titleLabel,
        hasAdditionalControl,
        isSomeSelected,
        clearSelected,
        labels.filters_clearAll_label,
        labels.common_confirm_button,
        SelectAll,
        isRightToLeft,
        Items,
    ]);
    return isDesktop ? ListDesktop : ListMobile;
});
CheckboxesDropdown.displayName = 'CheckboxesDropdown';
